import React, { FunctionComponent, MouseEvent, ReactNode, useCallback } from "react";

import { Text } from "@multiweb/reactComponents";
import { bridge } from "@multiweb/reactCore/bridge";
import { bemClassNames, EMPTY_OBJECT, transformKeysToParamCase } from "@multiweb/reactUtils";
import { OpenEditWindowParams } from "@multiweb/types/react";
import { LiteListFrameGeneralAttribute } from "../../../components/LiteListFrameGeneralAttribute";
import { FrameAttributeSharedProps } from "../../../types";
import { LOVShowAttrItemComponent } from "../LOVShowAttrItemComponent";

const { openAttributeEditWindow, stopPropagation } = bridge;

export type LOVAttributeComponentProps = {
    /** Názvy atributů zobrazených v jedné buňce. */
    showAttrs: string[];
    /** Informace potřebné k otevření editačního okna. */
    openEditWindowParams?: OpenEditWindowParams;
    /** Určuje, zda je komponenta vizuálně aktivní a zda s ní lze pracovat. */
    interactive?: boolean;
} & FrameAttributeSharedProps;

const classNames = bemClassNames("lov-attribute-component");

/**
 * Komponenta slouží pro zobrazení jednotlivých atributů LOV atributu.
 */
export const LOVAttributeComponent: FunctionComponent<LOVAttributeComponentProps> = ({
    recordId,
    dataSourceKey,
    attributeName,
    showAttrs,
    interactive = true,
    openEditWindowParams,
}) => {
    const hasEditWindow = !!openEditWindowParams;
    const onClick = useCallback(
        (e: MouseEvent<HTMLSpanElement>) => {
            if (openEditWindowParams) {
                openAttributeEditWindow(openEditWindowParams);
            }
            stopPropagation(e);
        },
        [openEditWindowParams],
    );

    const dividedAttrValues: Array<ReactNode> = [];
    showAttrs.forEach((showAttrName, index) => {
        const attrName = attributeName + "." + showAttrName.toLowerCase();
        const key = `${attrName}_${index}`;
        dividedAttrValues.push(
            <LiteListFrameGeneralAttribute
                attributeName={attrName}
                inline={true}
                AttributeComponent={LOVShowAttrItemComponent}
                {...{ key, dataSourceKey, recordId }}
            />,
        );

        if (index !== showAttrs.length - 1) {
            dividedAttrValues.push(
                <span key={`${key}_separator`} className={classNames("separator")}>
                    <Text value={" | "} disabled={!interactive} />
                </span>,
            );
        }
    });

    return (
        <span
            className={classNames(transformKeysToParamCase({ ellipsis: true, hasEditWindow, disabled: !interactive }))}
            {...(interactive ? { onClick } : EMPTY_OBJECT)}
            aria-disabled={!interactive}
        >
            {dividedAttrValues}
        </span>
    );
};
